import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";

import {
  isThalamosAdministrationSessionContext,
  type CurrentUser,
} from "@thalamos/haven-common";

import { useUser } from "../../Context/UserContext.js";
import { adminPageForPath, AdminPageSearch } from "./AdminPageSearch.js";

/**
 * Whether the session is acting in administration.
 *
 * Reads the context the session is *acting in*, not the memberships the user
 * holds: an administrator who has adopted a customer context to reproduce a
 * problem is acting as that customer, and the administrative area is not
 * theirs while they are.
 *
 * Decides only what to render — `authorise` checks every request behind it —
 * and defers to the shared `isThalamosAdministrationSessionContext` so the two
 * cannot drift apart.
 */
const isActingAsAdministrator = (user: CurrentUser | undefined): boolean =>
  isThalamosAdministrationSessionContext(user?.sessionContext);

export const AdminPage = () => {
  const { user } = useUser();

  return <AdminPageContent user={user} />;
};

/**
 * Exported separately so a story can render the shell against a session it chose
 * — including one that may not see it, which is a state worth being able to look
 * at rather than only assert about.
 */
export const AdminPageContent = ({
  user,
}: {
  user: CurrentUser | undefined;
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  if (!isActingAsAdministrator(user)) {
    return (
      <Typography variant="body1">{t("admin.notAnAdministrator")}</Typography>
    );
  }

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("admin.title")}
        </Typography>
        <ChevronRightIcon fontSize="small" color="disabled" />
        <AdminPageSearch current={adminPageForPath(pathname)} />
      </Box>

      <Outlet />
    </Stack>
  );
};
